import { Directive, ElementRef, forwardRef, HostListener, inject, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Directive({
  selector: 'input[appNumberFormat]',
  standalone: true,
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => NumberFormatDirective),
    multi: true
  }]
})
export class NumberFormatDirective implements ControlValueAccessor {
  @Input() decimals: number | null = null;
  @Input() min: number | null = null;
  @Input() max: number | null = null;

  private el = inject(ElementRef<HTMLInputElement>);
  private onChange: (value: number | null) => void = () => {};
  private onTouched: () => void = () => {};
  private numericValue: number | null = null;

  writeValue(value: any): void {
    if (value != null && value !== '' && !isNaN(value)) {
      this.numericValue = Number(value);
      const opts: Intl.NumberFormatOptions = this.decimals != null
        ? { minimumFractionDigits: this.decimals, maximumFractionDigits: this.decimals }
        : {};
      this.el.nativeElement.value = this.numericValue.toLocaleString('de-DE', opts);
    } else {
      this.numericValue = null;
      this.el.nativeElement.value = '';
    }
  }

  registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  @HostListener('focus')
  onFocus(): void {
    if (this.numericValue != null) {
      if (this.decimals != null) {
        this.el.nativeElement.value = this.numericValue.toFixed(this.decimals).replace('.', ',');
      } else {
        this.el.nativeElement.value = String(this.numericValue).replace('.', ',');
      }
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    const pasted = event.clipboardData?.getData('text')?.trim() ?? '';
    if (!pasted) return;

    const num = this.parseInternationalNumber(pasted);
    if (num === null) return;

    event.preventDefault();
    const rounded = this.clamp(this.applyDecimals(num));
    this.numericValue = rounded;
    this.onChange(rounded);
    // Input is focused during paste — show editing format (decimal comma),
    // matching onFocus (toFixed when decimals set, plain otherwise)
    this.el.nativeElement.value = (this.decimals != null
      ? rounded.toFixed(this.decimals)
      : String(rounded)
    ).replace('.', ',');
  }

  private applyDecimals(value: number): number {
    if (this.decimals == null) return value;
    const factor = Math.pow(10, this.decimals);
    return Math.round(value * factor) / factor;
  }

  private clamp(value: number): number {
    let v = value;
    if (this.min != null && v < this.min) v = this.min;
    if (this.max != null && v > this.max) v = this.max;
    return v;
  }

  /**
   * Parses a number string in any common international format and returns a
   * JS number. Supports:
   *   3.445.334,89  (de/el/it)   3,445,334.89 (en)
   *   3 445 334,89  (fr — incl. NBSP/NNBSP)   3'445'334.89 (ch)
   *   plain "3445334" / shorthand "334,89" / negatives / currency prefixes
   *   (123) / (1.234,56) — accounting convention for negatives
   * Returns null when the input cannot be confidently parsed as a number.
   */
  private parseInternationalNumber(raw: string): number | null {
    let s = raw
      .replace(/[€$£¥₹]/g, '')
      .replace(/[\s\u00A0\u202F']/g, '')
      .trim();

    let parenNegative = false;
    if (s.startsWith('(') && s.endsWith(')')) {
      parenNegative = true;
      s = s.slice(1, -1);
    }

    if (!s || /[^\d.,\-+]/.test(s)) return null;

    const negative = parenNegative !== s.startsWith('-');
    if (s.startsWith('-') || s.startsWith('+')) s = s.slice(1);

    const lastDot = s.lastIndexOf('.');
    const lastComma = s.lastIndexOf(',');

    if (lastDot === -1 && lastComma === -1) {
      const n = Number(s);
      return isFinite(n) ? (negative ? -n : n) : null;
    }

    if (lastDot !== -1 && lastComma !== -1) {
      // Both present — the rightmost separator is the decimal one.
      if (lastComma > lastDot) {
        s = s.replace(/\./g, '').replace(',', '.');
      } else {
        s = s.replace(/,/g, '');
      }
    } else {
      const sep = lastComma !== -1 ? ',' : '.';
      const parts = s.split(sep);
      const last = parts[parts.length - 1];
      // Multiple occurrences → thousands separator.
      // Single occurrence with exactly 3 trailing digits → also thousands,
      // UNLESS the integer part is "0" (e.g. "0.455" must be decimal).
      const isThousands =
        parts.length > 2 ||
        (parts.length === 2 && last.length === 3 && parts[0] !== '0');
      if (isThousands) {
        s = parts.join('');
      } else if (sep === ',') {
        s = s.replace(',', '.');
      }
    }

    const n = Number(s);
    return isFinite(n) ? (negative ? -n : n) : null;
  }

  @HostListener('blur')
  onBlur(): void {
    const raw = this.el.nativeElement.value.trim();
    const parsed = raw === '' ? null : this.parseInternationalNumber(raw);
    const newValue = parsed !== null ? this.clamp(this.applyDecimals(parsed)) : null;

    // Only notify the form if the value actually changed to avoid false dirty state
    if (newValue !== this.numericValue) {
      this.numericValue = newValue;
      this.onChange(newValue);
    }

    this.writeValue(this.numericValue);
    this.onTouched();
  }
}
